import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useNdfMoi } from '../../hooks/useNdfMoi';

/**
 * Shell de l'espace collaborateur.
 *
 * Il s'AJOUTE au layout de Gertrude, il ne le remplace pas : monté à part, il
 * faisait disparaître le menu principal, le logo et tout moyen de revenir à
 * l'accueil — l'utilisateur se retrouvait enfermé dans le module.
 *
 * Sous le point de rupture `md`, il ajoute une barre d'onglets basse et un
 * bouton « + » flottant, atteignables au pouce — ce qui compte quand on
 * déclare une dépense debout sur un quai. Au-delà, les deux disparaissent et
 * la navigation habituelle suffit.
 */

const ONGLETS = [
  { to: '/notes-de-frais/tableau-de-bord', libelle: 'Accueil', icone: '🏠' },
  { to: '/notes-de-frais/mes-depenses', libelle: 'Dépenses', icone: '🧾' },
  { to: '/notes-de-frais/mes-notes', libelle: 'Notes', icone: '📋' },
  { to: '/notes-de-frais/moi', libelle: 'Moi', icone: '👤' },
];

/** Onglet réservé aux approbateurs : inutile d'encombrer les autres. */
const ONGLET_VALIDATION = { to: '/notes-de-frais/a-valider', libelle: 'À valider', icone: '✅' };

export function NdfShell() {
  const navigate = useNavigate();
  const { peutValider } = useNdfMoi();
  // L'onglet de validation s'INSÈRE, il ne remplace rien : la version
  // précédente évinçait « Moi », et un approbateur perdait l'accès à son
  // récapitulatif kilométrique sans que rien ne l'explique.
  const onglets = peutValider
    ? [...ONGLETS.slice(0, 3), ONGLET_VALIDATION, ...ONGLETS.slice(3)]
    : ONGLETS;

  return (
    // pb-24 seulement sous `md` : au-delà, la barre d'onglets disparaît et
    // cette réserve d'espace n'aurait plus d'objet.
    <div className="min-h-full pb-24 md:pb-0">
      <div className="mx-auto max-w-2xl md:max-w-none">
        <Outlet />
      </div>

      {/* Bouton principal : un seul geste par écran, en zone basse. */}
      <button
        type="button"
        onClick={() => navigate('/notes-de-frais/depense/nouvelle')}
        aria-label="Nouvelle dépense"
        className="fixed bottom-24 right-5 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-nuit text-2xl text-white shadow-lg transition-transform active:translate-y-px md:hidden"
      >
        +
      </button>

      {/* pb-[env(safe-area-inset-bottom)] : en application installée sur
          iPhone, la barre doit rester au-dessus de l'indicateur d'accueil. */}
      {/* Barre d'onglets basse : SOUS le point de rupture uniquement. Au-delà,
          la navigation de Gertrude reprend la main : sans quoi on perd le menu
          principal, le logo, et tout moyen de revenir à l'accueil. */}
      <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-nuit/10 bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
        <ul className="mx-auto flex max-w-2xl">
          {onglets.map((o) => (
            <li key={o.to} className="flex-1">
              <NavLink
                to={o.to}
                className={({ isActive }) =>
                  // px-1 + truncate : à cinq onglets sur un écran de 375 px,
                  // « À valider » déborderait et pousserait les autres.
                  `flex min-h-16 flex-col items-center justify-center gap-0.5 px-1 text-center text-[11px] leading-tight ${
                    isActive ? 'font-semibold text-nuit' : 'text-nuit/50'
                  }`
                }
              >
                <span className="text-xl leading-none" aria-hidden>
                  {o.icone}
                </span>
                <span className="w-full truncate">{o.libelle}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
